import { Injectable } from '@angular/core';

/**
 * Onde a pessoa parou numa lista, descrito por ÂNCORA e não só por pixel.
 *
 * O pixel absoluto só se traduz em "aquele lugar do cardápio" se TUDO acima dele
 * estiver montado com a mesma altura de antes, e na volta de um item nada disso é
 * verdade: a lista rende progressivamente (menu-list.page.ts) e o carrossel de
 * recomendações entra ACIMA dela depois. A categoria + o deslocamento dentro dela dizem
 * QUAL conteúdo estava no topo da tela, e isso continua valendo quando a altura de cima
 * muda.
 */
export interface ScrollAnchor {
    /** id da categoria da lista geral encostada no topo da viewport; `null` = acima da 1ª (hero). */
    categoryId: string | null;
    /** índice dela na lista geral no momento da captura (diagnóstico). -1 = hero. */
    categoryIndex: number;
    /** px entre o topo daquela categoria e o topo da viewport (com `categoryId` null, igual a `absoluteY`). */
    offset: number;
    /** scrollTop no momento da captura — o último recurso, acima da lista (cabeçalho, barra). */
    absoluteY: number;
    /**
     * px entre o topo da LISTA (`app-menu-list`) e o topo da viewport — negativo quando a lista
     * começa abaixo do topo da tela, ainda dentro dela; `null` com a lista inteira abaixo da
     * tela. Vale para âncora de hero (destaques/combos/pizzas, antes da 1ª categoria) e é o
     * plano B quando a categoria não existe mais: o carrossel de recomendações e a barra sticky
     * ficam acima da lista e mudam de altura entre a saída e a volta, então o pixel absoluto
     * pousava deslocado pela altura deles.
     */
    listOffset: number | null;
}

/**
 * Sobrevive à destruição da página (`providedIn: 'root'`): no layout novo o
 * `app.component.html` usa `<router-outlet>` puro e as rotas de item são IRMÃS da rota
 * do cardápio, então a MenuPage é destruída e recriada a cada item aberto. Nada que
 * fique no componente atravessa isso.
 *
 * Só memória: um reload começa do topo, como sempre.
 */
@Injectable({ providedIn: 'root' })
export class ScrollSaveService {
    private positions = new Map<string, number>();
    private anchors = new Map<string, ScrollAnchor>();

    /** Posição em pixel, para listas que não têm âncora. O cardápio ('menu') usa `saveAnchor`. */
    save(key: string, position: number) {
        this.positions.set(key, position);
    }

    get(key: string): number {
        return this.positions.get(key) ?? 0;
    }

    /** Guarda a âncora. Quem não conseguiu medir NÃO chama isto: a última boa fica. */
    saveAnchor(key: string, anchor: ScrollAnchor) {
        this.anchors.set(key, anchor);
    }

    getAnchor(key: string): ScrollAnchor | null {
        return this.anchors.get(key) ?? null;
    }
}
